import { css } from '@emotion/react'
import { Stack, Typography } from '@mui/material'
import { CalendarStatus } from '@prisma/client'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { MouseEvent, useState } from 'react'
import { mutate } from 'swr'
import CalendarIcon from 'assets/icons/nucleo/18px_calendar.svg'
import ItineraryIcon from 'assets/icons/nucleo/18px_itinerary-2.svg'
import LocationIcon from 'assets/icons/nucleo/18px_location.svg'
import { usePriceFormat } from 'hooks/usePriceFormat'
import { AutocompleteItem } from 'interfaces/algolia'
import { apiClient } from 'services/api/client'
import { themeUtils } from 'styles/theme'
import {
  CardContainer,
  ImageContainer,
  ImageWrapper,
  InstantBadge,
  TopBar
} from './EventCard.style'
import { EventHeart } from './EventHeart'
import { useEventCard } from './Hook/useEventCard'
import { useFinalPrice } from './Hook/useFinalPrice'
import { NewFlexContainer } from './common/common.style'
import { Type } from './event/RaceCard/RaceCard.style'

const iconCss = css`
  min-width: 18px;
`

interface EventCardProps {
  isEventSelected: boolean | undefined
  priority?: boolean
  hit: AutocompleteItem
  highLighted?: boolean
  setSelectedEventId?: (eventId: number | null) => void
}

export const EventCard = ({
  isEventSelected,
  priority,
  hit,
  setSelectedEventId
}: EventCardProps) => {
  const { t } = useTranslation('common')

  const {
    _highlightResult: {
      objectID: { value: objectID }
    },
    eventName,
    eventCity,
    eventCoverImage,
    eventCountrySubdivisionDisplayCodeLevel2,
    editionCalendarStatus,
    eventLivePriceStartingFrom,
    eventSlug,
    eventIsFeatured
  } = hit
  const { formatPriceDefault } = usePriceFormat()
  const finalPrice = useFinalPrice(eventLivePriceStartingFrom * 100)

  const [eventCoverImg, setEventCoverImg] = useState<string | null | undefined>(
    eventCoverImage?.replace('http://', 'https://')
  )
  const toggleFavorite = async (event: MouseEvent<SVGElement>) => {
    event.preventDefault()
    await (isEventSelected ? apiClient.delete : apiClient.post)(
      `/user-event-favorite/${objectID}`
    )
    mutate('user-event-favorites')
    mutate('user-event-favorites-full')
  }

  const { dateDisplayText, distanceLabels, isOver, eventTypes } =
    useEventCard(hit)

  return (
    <CardContainer
      prefetch={false}
      href={`/event/${eventSlug}`}
      passHref
      onMouseEnter={() => {
        if (setSelectedEventId) {
          setSelectedEventId(Number(objectID))
        }
      }}
      onMouseLeave={() => {
        if (setSelectedEventId) {
          setSelectedEventId(null)
        }
      }}
    >
      <ImageContainer>
        <TopBar>
          {eventIsFeatured &&
          !isOver &&
          editionCalendarStatus === CalendarStatus.CONFIRMED ? (
            <InstantBadge>{t('event.featured.label')}</InstantBadge>
          ) : (
            <Stack />
          )}
          <NewFlexContainer
            css={css`
              svg {
                width: 24px;
                height: 24px;
              }
            `}
          >
            <EventHeart
              eventId={Number(objectID)}
              toggleFavorite={toggleFavorite}
              isUserEventFavoriteSelected={isEventSelected}
            />
          </NewFlexContainer>
        </TopBar>

        <ImageWrapper>
          {eventCoverImg && (
            <Image
              src={eventCoverImg}
              priority={priority}
              fetchPriority={priority ? 'high' : 'auto'}
              onError={() => {
                setEventCoverImg(null)
              }}
              alt={`${eventName} - image`}
              fill
              sizes="(max-width: 768px) 50vw, 25vw"
              style={{ objectFit: 'cover' }}
            />
          )}
        </ImageWrapper>
      </ImageContainer>
      <NewFlexContainer flexDirection="column" padding="12px 0" gap="12px">
        <Typography
          variant="h4"
          component="p"
          css={css`
            ${themeUtils.maxWidth.md} {
              overflow-wrap: anywhere;
            }
          `}
        >
          {eventName}
        </Typography>
        <NewFlexContainer
          flexDirection="column"
          justifyContent="space-between"
          gap="6px"
        >
          {(editionCalendarStatus !== CalendarStatus.CONFIRMED || isOver) && (
            <NewFlexContainer>
              <Type $color="onRed" $backgroundColor="red">
                {isOver
                  ? t('event.footer.over')
                  : editionCalendarStatus
                  ? t(editionCalendarStatus)
                  : ''}
              </Type>
            </NewFlexContainer>
          )}
          <NewFlexContainer gap="8px">
            <CalendarIcon css={iconCss} />
            <Typography variant="body2" fontSize={16}>
              {dateDisplayText}
            </Typography>
          </NewFlexContainer>
          {distanceLabels.length > 0 && (
            <NewFlexContainer gap="8px">
              <ItineraryIcon css={iconCss} />
              <Typography variant="body2" fontSize={16}>
                {distanceLabels.join(', ')}
              </Typography>
            </NewFlexContainer>
          )}
          <NewFlexContainer gap="8px">
            <LocationIcon css={iconCss} />
            <Typography variant="body2" fontSize={16}>
              {eventCity} ({eventCountrySubdivisionDisplayCodeLevel2})
            </Typography>
          </NewFlexContainer>
        </NewFlexContainer>
        {!!eventLivePriceStartingFrom && (
          <NewFlexContainer>
            <Typography variant="body2" fontSize={16} fontWeight={600}>
              {t('event.price.label')}
              {formatPriceDefault(finalPrice.amount, finalPrice.currency)}
            </Typography>
          </NewFlexContainer>
        )}
        <NewFlexContainer gap="8px" flexWrap="wrap">
          {eventTypes.map((typeObj, index) => (
            <Type
              key={index}
              $color={typeObj.color}
              $backgroundColor={typeObj.backgroundColor}
            >
              {t(typeObj.value as never)}
            </Type>
          ))}
        </NewFlexContainer>
      </NewFlexContainer>
    </CardContainer>
  )
}
